import React, { useState, useEffect, useMemo } from 'react';
import { RecordingSession, NavigationScreen } from '@/types';
import { formatDuration, formatFileSize, formatDate, generateThumbnailFromUrl, generateThumbnailFromBlob } from '@/utils/helpers';
import { StorageService } from '@/services/storageService';
import {
  Search,
  Filter,
  ArrowUpDown,
  LayoutGrid,
  List,
  Film,
  Users,
  Calendar,
  FolderOpen,
  Trash2,
  Edit2,
  Check,
  Play,
  Share2,
  Crop,
  ArrowRight,
  Download,
  Mic,
  Monitor,
  Layers,
  Sparkles
} from 'lucide-react';

export type RecordingCategory = 'all' | 'podcast' | 'group-meeting' | 'screen';

export const getRecordingCategory = (rec: RecordingSession): 'podcast' | 'group-meeting' | 'screen' => {
  if (
    rec.podcastMetadata ||
    rec.tags?.includes('podcast') ||
    rec.tags?.includes('vodcast') ||
    rec.tags?.includes('host-cam') ||
    rec.tags?.includes('guest-cam') ||
    rec.title.toLowerCase().startsWith('podcast') ||
    rec.title.toLowerCase().includes('vodcast') ||
    rec.title.toLowerCase().includes('episode')
  ) {
    return 'podcast';
  }

  if (
    rec.isGroupMeeting ||
    rec.tags?.includes('group-meeting') ||
    rec.title.toLowerCase().includes('group meeting') ||
    rec.title.toLowerCase().includes('meeting (') ||
    rec.title.toLowerCase().startsWith('group meeting')
  ) {
    return 'group-meeting';
  }

  return 'screen';
};

interface VideoThumbnailPreviewProps {
  recording: RecordingSession;
  onUpdateThumbnail?: (id: string, thumbnailUrl: string, resolvedFilePath?: string) => void;
  iconSize?: 'sm' | 'md' | 'lg';
}

export const VideoThumbnailPreview: React.FC<VideoThumbnailPreviewProps> = ({
  recording,
  onUpdateThumbnail,
  iconSize = 'lg'
}) => {
  const [thumbSrc, setThumbSrc] = useState<string | null>(recording.thumbnailUrl || null);
  const [isExtracting, setIsExtracting] = useState<boolean>(!recording.thumbnailUrl);

  useEffect(() => {
    if (recording.thumbnailUrl) {
      setThumbSrc(recording.thumbnailUrl);
      setIsExtracting(false);
      return;
    }

    let isCancelled = false;
    setIsExtracting(true);

    const extractThumbnail = async () => {
      // 1. First attempt Electron IPC (searches Videos, Downloads, relative paths and extracts frame 1 via FFmpeg)
      if (window.electronAPI && typeof window.electronAPI.getVideoThumbnail === 'function') {
        try {
          const res = await window.electronAPI.getVideoThumbnail({
            filePath: recording.filePath,
            title: recording.title,
            id: recording.id
          });
          if (!isCancelled && res && res.success && res.thumbnailUrl) {
            setThumbSrc(res.thumbnailUrl);
            setIsExtracting(false);
            onUpdateThumbnail?.(recording.id, res.thumbnailUrl, res.resolvedFilePath);
            return;
          }
        } catch (err) {
          console.warn('Electron thumbnail generation failed', err);
        }
      }

      // 2. Fallback to HTML5 video capture if blobUrl is available
      if (recording.blobUrl) {
        try {
          const generated = await generateThumbnailFromUrl(recording.blobUrl, 0.5);
          if (!isCancelled && generated) {
            setThumbSrc(generated);
            setIsExtracting(false);
            onUpdateThumbnail?.(recording.id, generated);
            return;
          }
        } catch (e) {
          console.warn('Browser blob thumbnail extraction failed', e);
        }
      }

      // 3. Fallback: If in Electron and filePath exists, read file buffer and extract
      if (window.electronAPI && recording.filePath && typeof window.electronAPI.readRecordingFile === 'function') {
        try {
          const buf = await window.electronAPI.readRecordingFile(recording.filePath);
          const blob = new Blob([buf], { type: 'video/webm' });
          const generated = await generateThumbnailFromBlob(blob, 0.5);
          if (!isCancelled && generated) {
            setThumbSrc(generated);
            setIsExtracting(false);
            onUpdateThumbnail?.(recording.id, generated);
            return;
          }
        } catch (err) {
          // File path not yet direct
        }
      }

      if (!isCancelled) {
        setIsExtracting(false);
      }
    };

    extractThumbnail();

    return () => {
      isCancelled = true;
    };
  }, [recording.id, recording.thumbnailUrl, recording.filePath, recording.title, recording.blobUrl]);

  if (thumbSrc) {
    return (
      <img
        src={thumbSrc}
        alt={recording.title}
        className="w-full h-full object-cover transition-opacity duration-300"
        onError={() => setThumbSrc(null)}
      />
    );
  }

  if (isExtracting) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black/40 animate-pulse">
        <Film className={`text-brand-muted/70 animate-spin ${iconSize === 'sm' ? 'w-4 h-4' : 'w-6 h-6'}`} />
      </div>
    );
  }

  return (
    <Film
      className={`text-brand-muted/70 group-hover:scale-105 transition-transform ${
        iconSize === 'sm' ? 'w-4 h-4' : 'w-8 h-8'
      }`}
    />
  );
};


interface RecordingLibraryScreenProps {
  recordings: RecordingSession[];
  onSelectRecording: (session: RecordingSession) => void;
  onUpdateRecording: (id: string, updates: Partial<RecordingSession>) => void;
  onDeleteRecording: (id: string) => void;
  onStartNewRecording: () => void;
  onNavigate?: (screen: NavigationScreen) => void;
}

export const RecordingLibraryScreen: React.FC<RecordingLibraryScreenProps> = ({
  recordings,
  onSelectRecording,
  onUpdateRecording,
  onDeleteRecording,
  onStartNewRecording,
  onNavigate
}) => {
  const [activeTab, setActiveTab] = useState<RecordingCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'duration' | 'size'>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editTitleText, setEditTitleText] = useState('');
  const [deleteConfirmSession, setDeleteConfirmSession] = useState<RecordingSession | null>(null);

  // Category counts for tab badges
  const counts = useMemo(() => {
    let podcast = 0;
    let groupMeeting = 0;
    let screen = 0;

    recordings.forEach((r) => {
      const cat = getRecordingCategory(r);
      if (cat === 'podcast') podcast++;
      else if (cat === 'group-meeting') groupMeeting++;
      else screen++;
    });

    return {
      all: recordings.length,
      podcast,
      'group-meeting': groupMeeting,
      screen
    };
  }, [recordings]);

  // Filtering: active tab + search query
  const filteredRecordings = useMemo(() => {
    return recordings.filter((r) => {
      // 1. Tab category filter
      if (activeTab !== 'all' && getRecordingCategory(r) !== activeTab) {
        return false;
      }
      // 2. Search query filter
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase().trim();
      const titleMatch = r.title.toLowerCase().includes(query);
      const participantMatch = r.participants?.some(p => p.name.toLowerCase().includes(query));
      const tagMatch = r.tags?.some(t => t.toLowerCase().includes(query));
      return titleMatch || participantMatch || tagMatch;
    });
  }, [recordings, activeTab, searchQuery]);

  // Sorting
  const sortedRecordings = useMemo(() => {
    return [...filteredRecordings].sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sortBy === 'duration') return b.durationSec - a.durationSec;
      if (sortBy === 'size') return b.fileSize - a.fileSize;
      return 0;
    });
  }, [filteredRecordings, sortBy]);

  const startRename = (r: RecordingSession) => {
    setEditingTitleId(r.id);
    setEditTitleText(r.title);
  };

  const handleThumbnailResolved = (id: string, thumbnailUrl: string, resolvedFilePath?: string) => {
    const updates: Partial<RecordingSession> = { thumbnailUrl };
    if (resolvedFilePath) {
      updates.filePath = resolvedFilePath;
    }
    onUpdateRecording(id, updates);
    StorageService.updateRecording(id, updates);
  };

  const saveRename = (id: string) => {
    if (editTitleText.trim()) {
      onUpdateRecording(id, { title: editTitleText.trim() });
    }
    setEditingTitleId(null);
  };

  const handleReveal = (filePath: string) => {
    if (window.electronAPI && filePath) {
      window.electronAPI.showItemInFolder(filePath);
    }
  };

  const handleDownloadRaw = async (rec: RecordingSession) => {
    const safeTitle = rec.title.replace(/[^a-zA-Z0-9_-]/g, '_');
    const filename = `${safeTitle}.mp4`;
    if (rec.blobUrl) {
      const a = document.createElement('a');
      a.href = rec.blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    if (window.electronAPI && rec.filePath) {
      try {
        const buf = await window.electronAPI.readRecordingFile(rec.filePath);
        const blob = new Blob([buf], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        return;
      } catch {
        window.electronAPI.showItemInFolder(rec.filePath);
      }
    }
  };

  const renderCategoryBadge = (rec: RecordingSession) => {
    const cat = getRecordingCategory(rec);
    if (cat === 'podcast') {
      return (
        <span className="px-2 py-0.5 rounded-md bg-purple-600/90 backdrop-blur-md text-white font-mono text-[9px] font-bold shadow-md flex items-center gap-1">
          <Mic className="w-2.5 h-2.5" />
          <span>PODCAST</span>
        </span>
      );
    }
    if (cat === 'group-meeting') {
      return (
        <span className="px-2 py-0.5 rounded-md bg-emerald-600/90 backdrop-blur-md text-white font-mono text-[9px] font-bold shadow-md flex items-center gap-1">
          <Users className="w-2.5 h-2.5" />
          <span>GROUP MEETING</span>
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-md bg-blue-600/90 backdrop-blur-md text-white font-mono text-[9px] font-bold shadow-md flex items-center gap-1">
        <Monitor className="w-2.5 h-2.5" />
        <span>SCREEN RECORD</span>
      </span>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-brand-border/60">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-brand-text">Recording Library</h1>
          <p className="text-xs text-brand-muted mt-0.5">
            Manage and view your separated recordings, podcasts, and group meetings
          </p>
        </div>

        <button
          onClick={onStartNewRecording}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-glow transition-all active:scale-95 cursor-pointer"
        >
          <Film className="w-3.5 h-3.5" />
          <span>New Recording</span>
        </button>
      </div>

      {/* Categorized Tabs Bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 select-none">
        {/* Tab: All */}
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'all'
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-600/25 border border-purple-400/30'
              : 'bg-brand-surface hover:bg-brand-elevated text-brand-muted hover:text-brand-text border border-brand-border/60'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>All Recordings</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold ${
            activeTab === 'all' ? 'bg-white/20 text-white' : 'bg-brand-elevated text-brand-muted'
          }`}>
            {counts.all}
          </span>
        </button>

        {/* Tab: Podcast Recordings */}
        <button
          onClick={() => setActiveTab('podcast')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'podcast'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-600/25 border border-purple-400/30'
              : 'bg-brand-surface hover:bg-brand-elevated text-brand-muted hover:text-brand-text border border-brand-border/60'
          }`}
        >
          <Mic className="w-3.5 h-3.5 text-purple-400" />
          <span>Podcast Recordings</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold ${
            activeTab === 'podcast' ? 'bg-white/20 text-white' : 'bg-brand-elevated text-purple-300'
          }`}>
            {counts.podcast}
          </span>
        </button>

        {/* Tab: Group Meeting Recordings */}
        <button
          onClick={() => setActiveTab('group-meeting')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'group-meeting'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-600/25 border border-emerald-400/30'
              : 'bg-brand-surface hover:bg-brand-elevated text-brand-muted hover:text-brand-text border border-brand-border/60'
          }`}
        >
          <Users className="w-3.5 h-3.5 text-emerald-400" />
          <span>Group Meeting Recordings</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold ${
            activeTab === 'group-meeting' ? 'bg-white/20 text-white' : 'bg-brand-elevated text-emerald-300'
          }`}>
            {counts['group-meeting']}
          </span>
        </button>

        {/* Tab: Screen Recordings */}
        <button
          onClick={() => setActiveTab('screen')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'screen'
              ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-600/25 border border-cyan-400/30'
              : 'bg-brand-surface hover:bg-brand-elevated text-brand-muted hover:text-brand-text border border-brand-border/60'
          }`}
        >
          <Monitor className="w-3.5 h-3.5 text-cyan-400" />
          <span>Screen Recordings</span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold ${
            activeTab === 'screen' ? 'bg-white/20 text-white' : 'bg-brand-elevated text-cyan-300'
          }`}>
            {counts.screen}
          </span>
        </button>
      </div>

      {/* Toolbar: Search, Sort & View Mode */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-brand-surface rounded-2xl border border-brand-border shadow-card">
        {/* Search */}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search recordings or participant names..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-brand-elevated border border-brand-border/60 rounded-xl pl-9 pr-3 py-1.5 text-xs text-brand-text focus:outline-none focus:border-brand-accent"
          />
        </div>

        {/* Sort Dropdown */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-3.5 h-3.5 text-brand-muted" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="bg-brand-elevated border border-brand-border rounded-xl px-2.5 py-1.5 text-xs text-brand-text focus:outline-none cursor-pointer"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="duration">Longest Duration</option>
            <option value="size">Largest File Size</option>
          </select>

          {/* Grid / List switch */}
          <div className="flex items-center gap-1 bg-brand-elevated p-1 rounded-xl border border-brand-border/60 ml-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-lg text-brand-muted transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-brand-surface text-brand-accent' : 'hover:text-brand-text'}`}
              title="Grid View"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-lg text-brand-muted transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-brand-surface text-brand-accent' : 'hover:text-brand-text'}`}
              title="List View"
            >
              <List className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Recordings Container */}
      {sortedRecordings.length === 0 ? (
        <div className="p-12 md:p-16 rounded-3xl bg-brand-surface/40 border border-dashed border-brand-border text-center space-y-4 max-w-lg mx-auto my-6">
          {activeTab === 'podcast' ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mx-auto shadow-lg shadow-purple-500/10">
                <Mic className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-sm md:text-base font-bold text-brand-text">No Podcast Recordings Yet</h3>
                <p className="text-xs text-brand-muted mt-1">
                  Start an interview, episode, or vodcast in Podcast Studio Pro to see recordings here.
                </p>
              </div>
              <button
                onClick={() => onNavigate ? onNavigate('podcast-studio') : onStartNewRecording()}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:brightness-110 text-white text-xs font-bold shadow-lg shadow-purple-600/30 transition-all cursor-pointer"
              >
                <Mic className="w-3.5 h-3.5" />
                <span>Launch Podcast Studio</span>
              </button>
            </>
          ) : activeTab === 'group-meeting' ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/10">
                <Users className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-sm md:text-base font-bold text-brand-text">No Group Meeting Recordings Yet</h3>
                <p className="text-xs text-brand-muted mt-1">
                  Host or join a meeting room. Active speaker and 1080p screen share recordings will appear here.
                </p>
              </div>
              <button
                onClick={() => onNavigate ? onNavigate('group-meeting') : onStartNewRecording()}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 text-white text-xs font-bold shadow-lg shadow-emerald-600/30 transition-all cursor-pointer"
              >
                <Users className="w-3.5 h-3.5" />
                <span>Open Group Meeting</span>
              </button>
            </>
          ) : activeTab === 'screen' ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center mx-auto shadow-lg shadow-cyan-500/10">
                <Monitor className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-sm md:text-base font-bold text-brand-text">No Screen Recordings Yet</h3>
                <p className="text-xs text-brand-muted mt-1">
                  Capture your full screen, specific window, and camera overlays with speaker separation.
                </p>
              </div>
              <button
                onClick={() => onNavigate ? onNavigate('studio') : onStartNewRecording()}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:brightness-110 text-white text-xs font-bold shadow-lg shadow-blue-600/30 transition-all cursor-pointer"
              >
                <Monitor className="w-3.5 h-3.5" />
                <span>Start Screen Recording</span>
              </button>
            </>
          ) : (
            <>
              <Film className="w-12 h-12 text-brand-muted mx-auto opacity-40" />
              <div>
                <h3 className="text-sm font-semibold text-brand-text">No recordings match your filter</h3>
                <p className="text-xs text-brand-muted mt-1">Try adjusting your search terms or start a new recording session.</p>
              </div>
              <button
                onClick={onStartNewRecording}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-bold shadow-glow transition-all cursor-pointer"
              >
                <Film className="w-3.5 h-3.5" />
                <span>Start New Recording</span>
              </button>
            </>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        /* GRID VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sortedRecordings.map((rec) => (
            <div
              key={rec.id}
              className="group p-4 rounded-3xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-brand-accent/40 shadow-card transition-all flex flex-col justify-between"
            >
              <div>
                {/* Thumbnail */}
                <div
                  onClick={() => onSelectRecording(rec)}
                  className="w-full aspect-video rounded-2xl bg-black/60 relative overflow-hidden mb-3 border border-brand-border/60 cursor-pointer flex items-center justify-center"
                >
                  <VideoThumbnailPreview
                    recording={rec}
                    onUpdateThumbnail={handleThumbnailResolved}
                    iconSize="lg"
                  />


                  {/* Category & Track Badges */}
                  <div className="absolute top-2 left-2 flex flex-col items-start gap-1 z-10">
                    {renderCategoryBadge(rec)}
                    {rec.tags?.includes('host-cam') || rec.title.includes('[HOST:') ? (
                      <span className="px-2 py-0.5 rounded-md bg-purple-900/90 backdrop-blur-md text-purple-200 font-mono text-[9px] font-bold shadow-md">
                        🎙️ HOST TRACK
                      </span>
                    ) : rec.tags?.includes('guest-cam') || rec.title.includes('[GUEST:') ? (
                      <span className="px-2 py-0.5 rounded-md bg-emerald-900/90 backdrop-blur-md text-emerald-200 font-mono text-[9px] font-bold shadow-md">
                        👥 GUEST TRACK
                      </span>
                    ) : null}
                  </div>

                  <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded-md bg-black/80 font-mono text-[10px] text-white">
                    {formatDuration(rec.durationSec)}
                  </span>
                </div>

                {/* Title & Rename */}
                {editingTitleId === rec.id ? (
                  <div className="flex items-center gap-1.5 mb-1">
                    <input
                      type="text"
                      value={editTitleText}
                      onChange={(e) => setEditTitleText(e.target.value)}
                      className="bg-brand-surface border border-brand-accent rounded px-2 py-0.5 text-xs text-brand-text w-full focus:outline-none"
                      autoFocus
                    />
                    <button
                      onClick={() => saveRename(rec.id)}
                      className="p-1 rounded bg-brand-accent text-white"
                    >
                      <Check className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-1 group/ttl">
                    <h3
                      onClick={() => onSelectRecording(rec)}
                      className="font-semibold text-xs md:text-sm text-brand-text group-hover:text-brand-accent transition-colors line-clamp-1 cursor-pointer"
                    >
                      {rec.title}
                    </h3>
                    <button
                      onClick={() => startRename(rec)}
                      className="opacity-0 group-hover/ttl:opacity-100 text-brand-muted hover:text-brand-text p-0.5 transition-opacity"
                      title="Rename"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <p className="text-[11px] text-brand-muted mt-1">
                  {formatDate(rec.createdAt)} • {formatFileSize(rec.fileSize)}
                </p>
              </div>

              {/* Bottom Card Controls */}
              <div className="pt-3 border-t border-brand-border/50 mt-4 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-brand-muted">
                  <Users className="w-3.5 h-3.5 text-brand-secondary" />
                  <span>{rec.participants?.length || 0} participants</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadRaw(rec);
                    }}
                    className="p-1.5 rounded-lg text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                    title="Download Raw MP4 Video directly"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => handleReveal(rec.filePath)}
                    className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text hover:bg-brand-surface"
                    title="Reveal file in Windows Explorer"
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirmSession(rec);
                    }}
                    className="p-1.5 rounded-lg text-brand-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-brand-surface transition-colors"
                    title="Delete recording"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => onSelectRecording(rec)}
                    className="p-1.5 rounded-lg text-brand-accent hover:bg-brand-accent/20"
                    title="Open recording"
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* LIST VIEW */
        <div className="space-y-2">
          {sortedRecordings.map((rec) => (
            <div
              key={rec.id}
              onClick={() => onSelectRecording(rec)}
              className="p-3.5 rounded-2xl bg-brand-surface hover:bg-brand-elevated border border-brand-border hover:border-brand-accent/40 shadow-sm transition-all flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-14 aspect-video rounded-lg bg-black/60 overflow-hidden shrink-0 flex items-center justify-center relative">
                  <VideoThumbnailPreview
                    recording={rec}
                    onUpdateThumbnail={handleThumbnailResolved}
                    iconSize="sm"
                  />
                </div>


                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-xs font-semibold text-brand-text truncate">{rec.title}</h4>
                    {renderCategoryBadge(rec)}
                    {rec.tags?.includes('host-cam') || rec.title.includes('[HOST:') ? (
                      <span className="px-1.5 py-0.2 rounded bg-purple-500/20 text-purple-300 font-mono text-[9px] font-bold shrink-0">
                        HOST
                      </span>
                    ) : rec.tags?.includes('guest-cam') || rec.title.includes('[GUEST:') ? (
                      <span className="px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 font-mono text-[9px] font-bold shrink-0">
                        GUEST
                      </span>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-brand-muted mt-0.5">
                    {formatDate(rec.createdAt)} • {formatDuration(rec.durationSec)} • {formatFileSize(rec.fileSize)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0 text-xs text-brand-muted">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadRaw(rec);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-medium transition-colors"
                  title="Download Raw MP4 File"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Download MP4</span>
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReveal(rec.filePath);
                  }}
                  className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text hover:bg-brand-surface"
                  title="Reveal file in Windows Explorer"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmSession(rec);
                  }}
                  className="p-1.5 rounded-lg text-brand-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-brand-surface transition-colors"
                  title="Delete recording"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Popup Modal */}
      {deleteConfirmSession && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl bg-brand-surface border border-brand-border shadow-elevated p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/15 border border-rose-500/25 text-rose-500 flex items-center justify-center shrink-0">
                <Trash2 className="w-6 h-6 animate-pulse" />
              </div>

              <div className="space-y-1">
                <h3 className="text-base font-bold text-brand-text">Delete Recording Permanently?</h3>
                <p className="text-xs text-brand-muted leading-relaxed">
                  Are you sure you want to delete <span className="font-semibold text-brand-text">"{deleteConfirmSession.title}"</span>?
                </p>
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-brand-elevated/60 border border-brand-border/40 text-xs space-y-1.5">
              <div className="flex items-center justify-between text-brand-muted">
                <span>Duration:</span>
                <span className="font-mono text-brand-text font-semibold">{formatDuration(deleteConfirmSession.durationSec)}</span>
              </div>
              <div className="flex items-center justify-between text-brand-muted">
                <span>File Size:</span>
                <span className="font-mono text-brand-text font-semibold">{formatFileSize(deleteConfirmSession.fileSize)}</span>
              </div>
              <div className="text-[11px] text-rose-400/90 pt-1 border-t border-brand-border/40">
                ⚠️ This will permanently remove the recording session and its extracted speaker tracks.
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmSession(null)}
                className="px-4 py-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  onDeleteRecording(deleteConfirmSession.id);
                  setDeleteConfirmSession(null);
                }}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold shadow-lg shadow-rose-600/30 transition-all active:scale-95 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Yes, Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
